import React, { useCallback, useState } from 'react';
import { addDays } from 'date-fns/addDays';
import { addWeeks } from 'date-fns/addWeeks';
import { addMonths } from 'date-fns/addMonths';
import {
  DayCalendarCell,
  DefaultMonthCalendarCell,
  WeekCalendarCell,
} from './components';
import MonthCalendar from './MonthCalendar';
import WeekCalendar from './WeekCalendar';
import DayCalendar from './DayCalendar';
import { getCalendarTimeIntervals } from './utils';
import { CALENDAR_VIEW_OPTIONS } from './constants';

export const useCalendar = (view = 'month') => {
  const [date, setDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState(view);
  let dateUpdater = addMonths;
  if (calendarView === 'day') {
    dateUpdater = addDays;
  }
  if (calendarView === 'week') {
    dateUpdater = addWeeks;
  }

  const next = useCallback(() => {
    setDate((current) => dateUpdater(current, 1));
  }, [dateUpdater, setDate]);

  const previous = useCallback(() => {
    setDate((current) => dateUpdater(current, -1));
  }, [dateUpdater, setDate]);

  const setToday = useCallback(() => setDate(new Date()), []);

  return [
    date,
    calendarView,
    { next, previous, setToday, setDate, setCalendarView },
  ];
};

/**
 * Calendar component to view dates for a single day, week, or month.
 *
 * @param props.date - the reference date for the calendar used to show the current day, week, or month
 * @param props.view - the view of the calendar (day, week, month)
 * @param props.MonthCellComponent - the cell component to be used when rendering the month view. Defaults to {@link DefaultDateCell}
 * @param props.WeekCellComponent - the cell component to be used when rendering the week view. Defaults to {@link WeekCalendarCell}
 * @param props.DayCellComponent - the cell component to be used when rendering the day view. Defaults to {@link WeekCalendarCell}
 * @param props.workingDaysOnly - if true, only show working days
 * @param props.skipWeekdays - array of weekday indices to skip (0-6, where 0 is Sunday)
 * @param props.skipLookup - object mapping date strings to skip status
 */
const Calendar = ({
  date,
  expandable = false,
  view = CALENDAR_VIEW_OPTIONS.month,
  timeIntervals = getCalendarTimeIntervals(),
  MonthCellComponent = DefaultMonthCalendarCell,
  WeekCellComponent = WeekCalendarCell,
  DayCellComponent = DayCalendarCell,
  workingDaysOnly = false,
  skipWeekdays = [],
  skipLookup = {},
  cellHeight,
  ...rest
}) => {
  if (view === CALENDAR_VIEW_OPTIONS.day) {
    return (
      <DayCalendar
        date={date}
        CellComponent={DayCellComponent}
        times={timeIntervals}
        workingDaysOnly={workingDaysOnly}
        skipWeekdays={skipWeekdays}
        skipLookup={skipLookup}
        {...rest}
      />
    );
  }
  if (view === CALENDAR_VIEW_OPTIONS.week) {
    return (
      <WeekCalendar
        date={date}
        CellComponent={WeekCellComponent}
        times={timeIntervals}
        workingDaysOnly={workingDaysOnly}
        skipWeekdays={skipWeekdays}
        skipLookup={skipLookup}
        cellHeight={cellHeight}
        {...rest}
      />
    );
  }
  return (
    <MonthCalendar
      expandable={expandable}
      date={date}
      CellComponent={MonthCellComponent}
      workingDaysOnly={workingDaysOnly}
      skipWeekdays={skipWeekdays}
      skipLookup={skipLookup}
      {...rest}
    />
  );
};

export default Calendar;
